import { FC, useEffect, useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { exitSlipsBySkills } from '../../graphQLOperations/ExitSlip';
import { ExitSlipExitSlipsBySkillsQuery } from '../../graphQLOperations/__generated__/ExitSlipExitSlipsBySkillsQuery.graphql';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { selectedTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { setExitSlipSkillProgress } from '../../redux/reducers/exitSlipSkillProgressSlice';
import { setSkillEarningExitSlipIds } from '../../redux/reducers/exitSlipSkillEarningSlice';
import { selectedExitSlipPendingFinish } from '../../redux/reducers/exitSlipPendingFinishSlice';
import {
  buildSkillExitSlipProgress,
  findSkillEarningExitSlipIds,
} from '../../businessLogicService/StudentDashboardAux/skillExitSlipReport';

interface InnerProps {
  studentId: string;
  targetGroupId: string;
}

/**
 * The query itself, split out so the guard above it can run before any hook does. Renders nothing:
 * its whole job is to put the report in redux for its two readers.
 */
const SkillExitSlipReport: FC<InnerProps> = ({ studentId, targetGroupId }) => {
  const dispatch = useAppDispatch();

  const { studentExitSlipReport } = useLazyLoadQuery<ExitSlipExitSlipsBySkillsQuery>(exitSlipsBySkills, {
    studentId,
    targetGroupId,
  });

  // A finish this device has seen that the report cannot: it was fetched once when the dashboard
  // opened. Folded into the badges so a ticket sat just now counts towards its skill straight away.
  const pendingFinish = useAppSelector(selectedExitSlipPendingFinish);

  const skillProgress = useMemo(
    () => buildSkillExitSlipProgress(studentExitSlipReport, pendingFinish),
    [studentExitSlipReport, pendingFinish],
  );
  // The other reader of the report, and deliberately not given the pending finish — see the note on
  // isLessonPassed for what showing it would cost the collapsed banner.
  const skillEarningExitSlipIds = useMemo(
    () => findSkillEarningExitSlipIds(studentExitSlipReport),
    [studentExitSlipReport],
  );

  useEffect(() => {
    dispatch(setExitSlipSkillProgress(skillProgress));
  }, [dispatch, skillProgress]);

  useEffect(() => {
    dispatch(setSkillEarningExitSlipIds(skillEarningExitSlipIds));
  }, [dispatch, skillEarningExitSlipIds]);

  return null;
};

/**
 * Feeds the two things the dashboard knows about a skill's exit tickets: the badge on each unit-path
 * card, and whether today's ticket is the last one standing between the student and a skill.
 *
 * Scoped to the student's whole roadmap (their target group), not to the unit on screen, and so
 * fetched once when the dashboard opens rather than again on every walk to another unit. That is what
 * makes the second reading possible at all: a skill one ticket away from being inferred is worth
 * announcing on today's banner wherever the student happens to be standing, and a unit-scoped report
 * could not see it unless they had already navigated to that unit.
 *
 * The badges stay unit-correct for free rather than by filtering: exitSlipSkillProgressSlice keys the
 * report by skill id and each card looks up its own skill, so the skills of other units sit in the
 * store unread. There is nothing to narrow.
 *
 * Renders nothing. Both readings travel through redux — the cards sit several levels inside the path
 * useDashboardUnitPath builds, and today's banner is in a sibling subtree entirely.
 */
export const DashboardSkillExitSlipReportQuery: FC = () => {
  const userRedux = useAppSelector(selectUser);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);

  if (!userRedux?.id || !targetGroupRedux?.id) {
    return null;
  }

  return <SkillExitSlipReport studentId={`StudentNode:${userRedux.id}`} targetGroupId={targetGroupRedux.id} />;
};

export default DashboardSkillExitSlipReportQuery;
